import flatten from 'lodash/flatten';
import flattenDeep from 'lodash/flattenDeep';
import {
  getStaticAssetUrl,
  StaticAssetType,
} from '../../../../../../common/shared/utils/getStaticAssetUrl';
import {FONT_FAMILY_FILE_NAME} from '../constant/talkViewerConstant';
import {ConvertedMedia} from '../type/ConvertedMedia.interface';
import {PreloadedMedia} from '../type/PreloadedMedia.interface';
import {PreloadType} from '../type/PreloadType.enum';
import {ProductInfo} from '../type/ProductInfo.interface';
import {Scene} from '../type/Scene.interface';
import {Talk} from '../type/Talk.interface';
import {TalkGroup} from '../type/TalkGroup.interface';
import {TalkViewerFontFamilyType} from '../type/TalkViewerFontFamilyType.enum';
import {convertMedia} from './convertMedia';
import {getIsSupportWebp} from './getIsSupportWebp';

function getIsNotPreloadedYet(preloadedUrls: string[], url: string) {
  return !preloadedUrls.includes(url);
}

/**
 * 씬, 톡그룹, 톡을 순회하며 미디어들을 미리 로드해 반환한다 \
 * 아직 이미지만 프리로드 하고 있음. \
 * 프리로드하는 이미지들
 * - 톡그룹의 백그라운드, 플로팅 이미지
 * - 캐릭터 프로필
 * - 톡의 미디어 이미지들
 *
 * @param TalkViewerState
 * @param productId
 */
export async function getPreloadedMedia({
  preloadType,
  productInfoMap,
  talkGroupMap,
  sceneMap,
  talkMap,
  productId,
  currentTalkIndex,
  isIos,
}: {
  preloadType: PreloadType;
  productInfoMap: {[key: number]: ProductInfo};
  talkGroupMap: {[key: string]: TalkGroup};
  sceneMap: {[key: string]: Scene};
  talkMap: {[key: string]: Talk};
  productId: number;
  isIos: boolean;
  currentTalkIndex: number;
}): Promise<PreloadedMedia> {
  const product = productInfoMap[productId];
  const {sceneIds} = product;
  const preloadedMedia: PreloadedMedia = {};
  const preloadedUrls: string[] = [];
  const talkGroupIds = flatten(
    sceneIds.map(sceneId => sceneMap[sceneId].talkGroupIds)
  );
  const allDataConvertedMediaPromises: Array<
    Promise<ConvertedMedia | undefined>
  > = [];
  talkGroupIds
    .map(talkGroupId => talkGroupMap[talkGroupId])
    .forEach(talkGroup => {
      // 톡그룹 백그라운드 이미지
      if (getIsNotPreloadedYet(preloadedUrls, talkGroup.background)) {
        preloadedUrls.push(talkGroup.background);
        allDataConvertedMediaPromises.push(
          convertMedia(preloadType, talkGroup.background)
        );
      }

      // 톡그룹 플로팅 이미지
      if (
        talkGroup.floatingImage &&
        getIsNotPreloadedYet(preloadedUrls, talkGroup.floatingImage)
      ) {
        preloadedUrls.push(talkGroup.floatingImage);
        allDataConvertedMediaPromises.push(
          convertMedia(preloadType, talkGroup.floatingImage)
        );
      }
    });

  const talkIds = flatten(
    talkGroupIds.map(talkGroupId => talkGroupMap[talkGroupId].talkIds)
  );

  // 기본폰트 preload
  allDataConvertedMediaPromises.push(
    convertMedia(
      preloadType,
      getStaticAssetUrl(
        FONT_FAMILY_FILE_NAME[TalkViewerFontFamilyType.Default],
        StaticAssetType.Font
      )
    )
  );
  preloadedUrls.push(FONT_FAMILY_FILE_NAME[TalkViewerFontFamilyType.Default]);

  const isSupportWebp = !isIos && (await getIsSupportWebp(isIos));

  talkIds
    .map(talkId => talkMap[talkId])
    .forEach(talk => {
      // 따로 사용하는 폰트가 있으면 해당 폰트 preload
      if (
        talk.font.family &&
        getIsNotPreloadedYet(
          preloadedUrls,
          FONT_FAMILY_FILE_NAME[talk.font.family]
        )
      ) {
        preloadedUrls.push(FONT_FAMILY_FILE_NAME[talk.font.family]);
        allDataConvertedMediaPromises.push(
          convertMedia(
            preloadType,
            getStaticAssetUrl(
              FONT_FAMILY_FILE_NAME[talk.font.family],
              StaticAssetType.Font
            )
          )
        );
      }

      if (
        !talk.media ||
        // 현재 톡에서 앞뒤로 index 3이상 차이나면 preload하지않음
        Math.abs(talk.talkIndex - currentTalkIndex) >= 3
      ) {
        return;
      }
      // webp를 지원하지 않으면 apng를 preload한다.
      // 톡 미디어 이미지
      const preloadUrl = isSupportWebp
        ? talk.media.source.url
        : talk.media.source.url.replace('webp', 'png');
      if (getIsNotPreloadedYet(preloadedUrls, preloadUrl)) {
        preloadedUrls.push(preloadUrl);
        allDataConvertedMediaPromises.push(
          convertMedia(preloadType, preloadUrl)
        );
      }
    });

  const dataConvertedMedias = flattenDeep(
    await Promise.all(allDataConvertedMediaPromises)
  );
  dataConvertedMedias.forEach(media => {
    if (!media) {
      return;
    }
    preloadedMedia[media.src] = media;
  });
  return preloadedMedia;
}
